import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import MenuButtonsPortal from '../../Menu/MenuButtonsPortal';
import BiButton from '../../primitives/buttons/BiButton.primitive';
import LoaderContainer from '../../shared/loaderContainer/LoaderContainer';
import { ITableHeader } from '../../shared/tables/models/ITableHeader';
import TemplateTable from '../../shared/tables/TemplateTable';
import { TableContainer } from '../../Organizations/OrganizationsList';
import { HTTP_STATUS } from '../../../http/constants/http.status';
import { AppConstants } from '../../../constants/AppConstants';
import { useTheme } from '../../../context/themeContext';
import { setBreadcrump } from '../../../app/organizationReducer';
import { useNavigate, useParams } from 'react-router-dom';
import { setToastData } from '../../../app/toastReducer';
import {
  setCareTeamsList,
  setCareplanConfig,
  setCareplanData,
  setConditionsList,
  setDeviceList,
  setListData,
  setNotesData,
  setOrgList,
  setPatientUserList,
  setPractitionerList,
  setPractitionerRoleList
} from '../../../app/carePlanReducer';
import {
  cloneDeep,
  findIndex,
  capitalize,
  filter,
  map,
  find,
  uniqBy,
  isEmpty,
  isEqual,
  compact
} from 'lodash';
import { getFailureMessage } from '../../../util/ErrorUtil';
import { RouteConstants } from '../../../constants/RouteConstants';
// import DeleteConfirmModal from './DeleteConfirmModal';
import { EllipsisDiv } from '../../../styles/globalStyles';

import { setTableOptions, resetTableOptions } from '../../../app/templateTableReducer';

import {
  filterOrgNames,
  getCarePlanOrganizations,
  getCareTeamsList,
  fetchCustomRoleUserList,
  fetchDeviceList,
  fetchPractitionerRole,
  filterAuthorData,
  configureCareplanCategories,
  getCareplanListData
} from '../carePlanUtils';
import {
  deleteCareplanNote,
  getCarePlan,
  listCareplanNotes
} from '../../../http/careplan-services';
import moment from 'moment';
import { customAddCircle } from '../../../assets/components/svgs';
import { useDidMountEffect } from '../../../util/customHooks';
import { getOrganization } from '../../../http/organization-services';
import Modal from '../../shared/ModalPortal/Modal';
import { ExclamationIcon } from '@heroicons/react/outline';
import { updateSelectedFilter } from '../../ConfigurationService/Configutils';
import { MenuOptions } from '../../primitives/dotMenu/ContextMenu';
import { MenuConstants } from '../../../constants/MenuConstants';
import { checkFeaturePermission } from '../../../util/permissionUtils';

/**
 * @parentId careplan
 * @manager Careplan Notes Listing
 * @overview Lists Notes under a Careplan
 *   <section>
 *       <p>
 *          Notes listing page list all the Notes under a Careplan. User can navigate to Notes listing page by clicking <b>Care Plan Template</b> navigation menuitem under the <strong>Platform Core</strong> menu group of admin portal left sidebar and then clicking on the dot menu option of the careplan table rowitem and selecting <strong>View Notes</strong>.
 *          The Default pagination size be fixed to 20 items, which can be changed to 50 or 100 from the dropdown menu of datagrid footer (The paging options are defined from the configuration). Datagrid footer will also display the current page and total number of pages available based on the choosen page size & Notes data.
 *       </p>
 *       <br>
 *       <p>
 *          Notes can be filtered based on Author Organization, Author Type and Author however sorting is only supported on Date/Time field. Admin user(s) can create new Note by clicking on <b>Add a Note</b> button provided on the admin portal toolbar,
 *           which will navigate user to Note creation page. <b>Add a Note</b> button will be shown based on logged in user role API permissions as described below.
 *       </p>
 *       <br>
 *       <p>
 *           Datagrid will display details of each note as row item with each column representing below object property.
 *           <br>
 *           <ul>
 *               <li>Note Content</li>
 *               <li>Author</li>
 *               <li>Author Type</li>
 *               <li>Organization</li>
 *               <li>Date/Time</li>
 *               <li>Last Modified On</li>
 *           </ul>
 *       </p>
 *     </section>
 * <section>
 *         <p>
 *          <h4>Table RowItem Dot Menu Options</h4>
 *         Notes listing table will have the following options in individual row items:
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Description</th>
 *               <th>Permission Needed</th>
 *            </tr>
 *             <tr>
 *               <td>Delete</td>
 *               <td>Deletes the Note. On selection a confirmation popup appears.</td>
 *               <td>careplan-service.careplan.notes.delete</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 * </section>
 *       <section>
 *         <p>
 *          <h4>Configuration based options</h4>
 *          Careplan Notes dropdown options in filters are configured via the System Setting with category "careplan.template.default.values". User can configure these options in the system settings for the following fields in listing:
 *        <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Key</th>
 *            </tr>
 *             <tr>
 *               <td>Author Type</td>
 *               <td>careplan.template.notes.author.types</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *     <section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes the List Careplan Status Code information.
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>CPS_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>CPS_INTERNAL_ERROR</td>
 *               <td>Internal Server Error</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td></td>
 *               <td>Service Unavailable</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of Notes listing page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) with specific version(s) for careplan listing</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>Careplan Service</td>
 *               <td>1.3.0</td>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
 *           <tr>
 *               <td>Configuration Service</td>
 *               <td>1.6.0</td>
 *         </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API role permissions for careplan listing page</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>/careplans</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>/careplans/:id</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.get</td>
 *               <td>Yes</td>
 *           </tr>
 *             <tr>
 *               <td>/careplans/:id/notes</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.note.list</td>
 *               <td>Yes</td>
 *            </tr>
 *            <tr>
 *               <td>/careplans/:id/notes/:id</td>
 *               <td>DELETE</td>
 *               <td>careplan-service.careplan.note.delete</td>
 *               <td>No</td>
 *            </tr>
 *       </table>
 *   </section>
 *   <section>
 *   </section>
 */

const CarePlanNotesList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();

  const [rows, setRows] = useState<any>([]);
  const [paging, setPaging] = useState<any>(null);
  const [size, setSize] = useState<number>(AppConstants.DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(AppConstants.DEFAULT_PAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [organizations, setOrganizations] = useState<Map<string, any>>(new Map());
  const [loadedOrgIds, setLoadedOrgIds] = useState<Array<string>>([]);
  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>('');

  const globalFilters: any = useAppSelector((state) => state.templateTableReducer.tableOptions);
  const careplanData = useAppSelector((state) => state.carePlanData.careplanData);
  const carePlanOrgList: any = useAppSelector((state) => state.carePlanData.orgList) || [];
  const careplanConfig: any = useAppSelector((state) => state.carePlanData.careplanConfig) || {};
  const listData: any = useAppSelector((state) => state.carePlanData.listData);

  const tableKey: string = 'careplan-notes-list';

  const [filterPillsData, setFilterPillsData] = useState<Array<any>>([]);
  const [loadNextData, setLoadNextData] = useState<any>({
    orgPaging: false,
    searchOrgName: '',
    searchOrg: false,
    searchAuthorName: '',
    searchAuthor: false,
    searchAuthorType: '',
    fieldValues: ''
  });

  const searchOrgData = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchOrgName: data,
      searchOrg: true,
      searchAuthorName: '',
      searchAuthor: false,
      searchAuthorType: ''
    }));
  };

  const searchAuthorData = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchOrgName: '',
      searchOrg: false,
      searchAuthorName: data,
      searchAuthor: true
    }));
  };

  const fetchMoreOrgData = () => {
    setLoadNextData((prevState: any) => ({ ...prevState, orgPaging: true }));
  };

  const searchFuncMapping: any = {
    orgFilter: searchOrgData,
    authorId: searchAuthorData
  };

  const fetchDependentFields = async (selectedItem: any) => {
    if (selectedItem.length) {
      let index = findIndex(filterList, function (item: any) {
        return item?.id === 'authorId';
      });
      const [item] = selectedItem;
      const { apiConfig, key } = item || {};
      const { fieldKey, fieldValues } = apiConfig || {};
      const listDataClone: any = cloneDeep(listData);

      if (index !== -1) {
        let element = cloneDeep(filterList[index]);
        element.itemsList = [];
        element.fieldValues = fieldValues;
        element.fieldKey = fieldKey;

        setFilterList((prevState: any) =>
          prevState.map((item: any) => (item.id === element.id ? { ...item, ...element } : item))
        );

        if (key && !listDataClone?.[key]?.length) {
          const data = await getCareplanListData(apiConfig);
          if (data.length) {
            listDataClone[key] = data;
          }
        }

        element.itemsList = listDataClone?.[key];

        setFilterList((prevState: any) =>
          prevState.map((item: any) => (item.id === element.id ? { ...item, ...element } : item))
        );
        if (!isEqual(listData, listDataClone)) {
          dispatch(setListData(listDataClone));
        }

        setLoadNextData((prevState: any) => ({
          ...prevState,
          searchAuthorType: key,
          fieldValues
        }));
      }
    } else {
      let index = findIndex(filterList, function (item: any) {
        return item?.id === 'authorId';
      });
      if (index !== -1) {
        let element = cloneDeep(filterList[index]);
        element.itemsList = [];
        element.fieldValues = 'display';

        setFilterList((prevState: any) =>
          prevState.map((item: any) => (item.id === element.id ? { ...item, ...element } : item))
        );

        setLoadNextData((prevState: any) => ({
          ...prevState,
          searchAuthorType: '',
          fieldValues: ''
        }));
      }
    }
  };

  const notesFiltersList = [
    {
      id: 'orgFilter',
      label: 'T_CARE_PLAN_NOTES_AUTHOR_ORG_FILTER',
      component: 'picklist',
      type: 'single',
      itemsList: [],
      selectedFilter: [],
      fieldKey: 'name',
      fieldValues: 'name',
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: true,
      fetchMoreData: fetchMoreOrgData,
      searchData: searchFuncMapping.orgFilter
    },
    {
      id: 'authorType',
      label: 'T_CARE_PLAN_NOTES_AUTHOR_TYPE_FILTER',
      component: 'picklist',
      type: 'single',
      selectedFilter: [],
      fieldKey: 'key',
      fieldValues: 'display',
      dependentChildId: 'authorId',
      disabled: false,
      hasMore: false,
      paging: { size: 25, page: 1 },
      searchable: false,
      fetchDependentFields: fetchDependentFields,
      isDependentFilter: true,
      searchData: searchFuncMapping.authorType,
      itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES]
    },
    {
      id: 'authorId',
      label: 'T_CARE_PLAN_NOTES_AUTHOR_FILTER',
      component: 'picklist',
      type: 'single',
      itemsList: [],
      selectedFilter: [],
      displayDelimiter: ' ',
      fieldKey: 'id',
      fieldValues: 'name',
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: true,
      searchData: searchFuncMapping.authorId
    }
  ];

  const [filterList, setFilterList] = useState<Array<any>>(notesFiltersList);

  const updateFilterSetData = (orgResponse: any, type: string) => {
    let initialFilterList: any = [...filterList];

    const currentFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === type) {
        return {
          ...obj,
          itemsList: orgResponse
        };
      } else {
        return { ...obj };
      }
    });
    setFilterList(currentFilterList);
  };

  const loadOrgSearchData = () => {
    const orgResponse = filterOrgNames(loadNextData.searchOrgName, carePlanOrgList);
    updateFilterSetData(orgResponse, 'orgFilter');
    updateSelectedFilter(
      filterList,
      filterPillsData,
      '',
      fetchMoreOrgData,
      setFilterPillsData,
      () => {} //setFilterList
    );
  };

  const getAuthorSearchKey = (type: string) => {
    let element = find(careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES], {
      key: type
    });
    if (element) {
      let key = element?.apiConfig?.fieldValues;
      return { data: listData?.[type], searchKey: key || [] };
    } else {
      return { data: [], searchKey: [] };
    }
  };

  const loadAuthorSearchData = () => {
    const { data, searchKey } = getAuthorSearchKey(loadNextData.searchAuthorType);
    const filteredResponse = filterAuthorData(data, loadNextData.searchAuthorName, searchKey);
    updateFilterSetData(filteredResponse, 'authorId');
  };

  useDidMountEffect(async () => {
    if (loadNextData.searchOrg) {
      loadOrgSearchData();
    }
    if (loadNextData.searchAuthor) {
      loadAuthorSearchData();
    }
  }, [loadNextData]);

  const setBreadcrumps = () => {
    dispatch(
      setBreadcrump([
        {
          key: RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_LIST.replace(':id', params?.id || ''),
          label: `${t('T_CARE_PLAN_VIEW_NOTE')}`
        }
      ])
    );
  };

  useEffect(() => {
    if (!isEmpty(careplanData)) {
      setBreadcrumps();
    } else {
      setIsLoading(true);
      getCareplanData();
    }
  }, [careplanData]);

  const checkConfiguration = async () => {
    let config = await configureCareplanCategories();
    if (!isEmpty(config)) {
      dispatch(setCareplanConfig(config));
    }
  };

  const getCareplanData = async () => {
    async function init() {
      try {
        let response = await getCarePlan(params?.id);
        const { status, data } = response;
        if (HTTP_STATUS.isSuccess(response?.status)) {
          dispatch(setCareplanData(data));
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
          setIsLoading(false);
          navigate(-1);
        }
      } catch (e) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: t('T_ERROR_SOMETHING_WRONG'),
            toastType: 'error'
          })
        );
        setIsLoading(false);
        navigate(-1);
      }
    }
    if (
      (params?.id && !isEmpty(careplanData) && params.id !== careplanData?.id) ||
      (isEmpty(careplanData) && params?.id)
    ) {
      await init();
    }
  };

  const updatePillsData = (appliedFilters: any, appliedFilterList: any) => {
    const pillsData: any = [];
    if (appliedFilterList && appliedFilterList.length) {
      appliedFilterList.forEach((key: any) => {
        switch (key) {
          case 'orgFilter':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item.name,
                  label: t('T_ORGANIZATION'),
                  id: key,
                  key: item.id
                });
              });
            }

            break;
          case 'authorType':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item?.display || item?.value || '',
                  label: t('T_CARE_PLAN_NOTES_AUTHOR_TYPE_FILTER'),
                  id: key,
                  key: item.key
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }

            break;

          case 'authorId':
            let fieldValues = getDisplayKey(appliedFilters?.authorType?.[0]) || [];
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name:
                    compact(fieldValues.map((fieldValue: string) => item[fieldValue])).join(' ') ||
                    item?.id,
                  label: t('T_CARE_PLAN_NOTES_AUTHOR_FILTER'),
                  id: key,
                  key: item.id
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }

            break;
        }
      });
    }

    return pillsData;
  };

  const initializeLoadNextData = (filters: any) => {
    const { authorType = [] } = filters || {};
    if (authorType.length && !loadNextData.searchAuthorType) {
      let [configItem] = authorType;
      const { apiConfig, key } = configItem || {};
      const { fieldKey, fieldValues } = apiConfig || {};
      setLoadNextData((prevState: any) => ({
        ...prevState,
        searchAuthorType: key,
        fieldValues
      }));
    }
  };

  const updateFilterListSelected = async (appliedFilters: any) => {
    const initialFilterList: any = [...filterList];
    const updatedFilterList = initialFilterList.map((filterObj: any) => {
      const selectedFilter = appliedFilters[filterObj.id] ? appliedFilters[filterObj.id] : [];
      return { ...filterObj, selectedFilter };
    });

    const authorTypeFilter = updatedFilterList.find((filter: any) => filter.id === 'authorType');

    const orgFilterIndex = updatedFilterList.findIndex((filter: any) => filter.id === 'orgFilter');

    const orgFilter = orgFilterIndex !== -1 ? updatedFilterList[orgFilterIndex] : {};

    const authorIdFilterIndex = updatedFilterList.findIndex(
      (filter: any) => filter.id === 'authorId'
    );
    const authorIdFilter = authorIdFilterIndex !== -1 ? updatedFilterList[authorIdFilterIndex] : {};

    if (!orgFilter?.itemsList?.length) {
      orgFilter.itemsList = [...carePlanOrgList];
      updatedFilterList[orgFilterIndex] = orgFilter;
    }

    if (authorTypeFilter?.selectedFilter?.length > 0 && !authorIdFilter?.itemsList?.length) {
      let [configItem] = authorTypeFilter?.selectedFilter;
      const { apiConfig, key } = configItem || {};
      const { fieldKey, fieldValues } = apiConfig || {};
      const listDataClone: any = cloneDeep(listData);
      if (key && !listDataClone?.[key]?.length) {
        const data = await getCareplanListData(apiConfig);
        if (data.length) {
          listDataClone[key] = data;
        }
      }
      authorIdFilter.itemsList = listDataClone?.[key];
      authorIdFilter.fieldKey = fieldKey;
      authorIdFilter.fieldValues = fieldValues;
      updatedFilterList[authorIdFilterIndex] = authorIdFilter;
      if (!isEqual(listData, listDataClone)) {
        dispatch(setListData(listDataClone));
      }
    }

    return updatedFilterList;
  };

  const [paramObj, setParamObj] = useState({
    orgFilter: '',
    authorType: '',
    authorId: ''
  });

  const updateParamObj = (appliedFilters: any, appliedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    const initialFilterList: any = [...filterList];
    initialFilterList.forEach((filterObj: any) => {
      switch (filterObj.id) {
        case 'orgFilter':
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'id');
          break;
        case 'authorType':
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'key');
          break;
        case 'authorId':
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'key');
          break;
      }
    });

    return initialParamObj;
  };

  const applyFilters = async (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setIsLoading(true);
    const appliedFilterList = Object.keys(filters);
    setFilterPillsData(updatePillsData(filters, appliedFilterList));
    initializeLoadNextData(filters);
    setFilterList(await updateFilterListSelected(filters));
    setParamObj(updateParamObj(filters, appliedFilterList));
    getNotesList(page, size, sortOrder, sortType, filters);
  };

  const getFieldKey = (filters: any) => {
    const { authorType = [] } = filters || {};
    if (authorType.length) {
      let [configItem] = authorType;
      const { apiConfig, key } = configItem || {};
      const { fieldKey = '' } = apiConfig || {};
      return fieldKey;
    }
    return '';
  };

  const getNotesList = async (
    page: number,
    size: number,
    sort: string = AppConstants.DEFAULT_SORT_BY,
    sortType: string = AppConstants.DEFAULT_SORT_DESC,
    filters?: any
  ) => {
    let query = '';

    if (!isEmpty(filters)) {
      let fieldKey = getFieldKey(filters);
      if (filters['authorType'] && filters['authorType'].length) {
        const [authorType] = filters['authorType'];
        query += `&author-type=${authorType.key}`;
      }

      if (filters['orgFilter'] && filters['orgFilter'].length) {
        const [orgFilter] = filters['orgFilter'];
        if (orgFilter?.id) query += `&author-org-id=${orgFilter.id}`;
      }

      if (filters['authorId'] && filters['authorId'].length) {
        const [authorEle] = filters['authorId'];
        if (authorEle?.[fieldKey]) query += `&author-id=${authorEle?.[fieldKey]}`;
      }
    }
    const response = await listCareplanNotes(
      params.id || '',
      page,
      size,
      [`${sort},${sortType}`],
      query
    );

    const { status, data } = response;
    if (status === HTTP_STATUS.HTTP_OK) {
      const { data: notes = [], paging } = data;
      let rows = cloneDeep(notes);
      await getOrgIdsArr(rows);

      if (rows.length) {
        const listDataClone = cloneDeep(listData);
        const updatedRows = [];

        for (let ele of rows) {
          let row = cloneDeep(ele);
          if (!listDataClone?.[row?.authorType]?.length) {
            let data = await getAuthorDetails(row, listDataClone);
            listDataClone[row?.authorType] = data;
          }

          let authorTypeEle = find(
            careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES],
            { key: row?.authorType }
          );

          if (authorTypeEle) {
            const { apiConfig } = authorTypeEle || {};
            const { fieldValues } = apiConfig || {};
            let item = find(
              listDataClone[row?.authorType],
              (author) => author.id === row.authorId || author.userId === row.authorId
            );
            row.authorName = fieldValues?.length
              ? compact(fieldValues.map((fieldValue: any) => item?.[fieldValue])).join(' ')
              : item?.serialNumber || item?.title || item?.name || '';
          }

          updatedRows.push(row);
        }

        if (!isEqual(listData, listDataClone)) {
          dispatch(setListData(listDataClone));
        }

        setRows(updatedRows);
      } else {
        setRows(`${t('T_NO_DATA_AVAILABLE_MSG')}`);
      }

      setIsLoading(false);
      setPaging(paging);
    } else {
      setIsLoading(false);
      setRows(getFailureMessage(response));
    }
  };

  const fetchDataAndUpdateOptions = async (
    dispatchFunction: Function,
    list: Array<any>,
    fetchDataFunction: Function
  ) => {
    if (!list.length) {
      const data = await fetchDataFunction();
      dispatch(dispatchFunction(data));
      return data;
    } else {
      return list;
    }
  };

  const getAuthorDetails = async (data: any, listData: any) => {
    const { authorType } = data || {};
    if (!authorType) {
      return [];
    }
    const configData = find(
      careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES],
      { key: authorType }
    );

    const { apiConfig } = configData || {};
    const { fieldKey = '' } = apiConfig || {};

    if (!listData?.[authorType]?.length) {
      const listResponse = await getCareplanListData(apiConfig);
      return listResponse;
    } else {
      return listData?.[authorType]?.length;
    }
  };

  const getMenuOptions = (data: any): Array<any> => {
    let optionList: any = [];
    if (checkFeaturePermission(AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_DELETE, false)) {
      optionList.push({
        display: `${t('T_DELETE')}`,
        key: MenuConstants.MENU_CARE_PLAN_NOTES,
        action: () => {
          setDeletePopup(true);
          setDeleteId(data?.id);
        }
      });
    }

    return optionList;
  };

  const tableHeadersBuilder = () => {
    let TABLE_HEADERS: Array<ITableHeader> = [
      {
        key: 'text',
        description: 'T_CARE_PLAN_NOTES_CONTENT',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (row: any) => {
          return (
            <EllipsisDiv className="w-full flex-1" title={row.text}>
              {row.text}
            </EllipsisDiv>
          );
        },
        width: '25%',
        styles: { borderRight: 'none' }
      },
      {
        key: 'options',
        description: '',
        sortEnabled: false,
        width: '7%',
        nastedData: true,
        clickable: false,
        render: (data: any) => {
          const menuOptions: Array<any> = getMenuOptions(data);
          return menuOptions.length ? (
            <div className="hover:bg-[#d9d9d9]" style={{ borderRadius: '10px' }}>
              <MenuOptions options={menuOptions} folderData={data} />
            </div>
          ) : (
            <div />
          );
        },
        sortingInformation: {
          order: ''
        },
        styles: {
          paddingRight: '0',
          paddingLeft: '0',
          borderLeft: 'none',
          rowStyle: { fontWeight: 'normal', padding: '0px', paddingLeft: '30px' }
        }
      },
      {
        key: 'author',
        description: 'T_CARE_PLAN_AUTHOR',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (row: any) => {
          let text = row?.authorName;
          return (
            <EllipsisDiv className="w-full flex-1" title={text}>
              {text}
            </EllipsisDiv>
          );
        },
        width: '15%'
      },
      {
        key: 'type',
        description: 'T_CARE_PLAN_AUTHOR_TYPE',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (row: any) => {
          return (
            careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES]?.find(
              (item: any) => item.key === row.authorType
            )?.display || row.authorType
          );
        },
        width: '10%'
      },

      {
        key: 'authorOrgId',
        description: 'T_ORGANIZATION',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          const { authorOrgId = '' } = data;
          let orgData = organizations.get(authorOrgId);
          if (!isEmpty(orgData)) {
            return (
              <EllipsisDiv className="w-full flex-1" title={orgData?.name}>
                {orgData?.name}
              </EllipsisDiv>
            );
          }
        },
        width: '15%'
      },

      {
        key: 'time',
        description: 'T_DATE_AND_TIME',
        sortEnabled: true,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return data.time ? moment(data.time).format(AppConstants.DEFAULT_DATE_FORMAT) : '';
        },
        width: '15%'
      },
      {
        key: 'updatedOn',
        description: 'T_LAST_MODIFIED_ON',
        sortEnabled: false,
        clickable: true,
        render: (data: any) => {
          return data.updatedOn
            ? moment(data.updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT)
            : '';
        },
        width: '15%',
        sortingInformation: {
          order: ''
        }
      }
    ];

    return TABLE_HEADERS;
  };

  const [tableHeaders, setTableHeaders] = useState<any>(tableHeadersBuilder());

  useEffect(() => {
    if (isEmpty(careplanConfig)) {
      checkConfiguration();
      return;
    }
    setFilterList(notesFiltersList);
    if (!isEmpty(globalFilters[tableKey])) {
      const { tableFilters, searchText, currentPage, size, sortName, sortType } =
        globalFilters[tableKey];
      applyFilters(searchText, currentPage, size, sortName, sortType, tableFilters);
    } else {
      dispatch(
        setTableOptions({
          [tableKey]: {
            ...globalFilters[tableKey],
            searchText: '',
            currentPage: AppConstants.DEFAULT_PAGE,
            size: AppConstants.DEFAULT_PAGE_SIZE,
            sortName: AppConstants.DEFAULT_SORT_BY,
            sortType: AppConstants.DEFAULT_SORT_DESC,
            tableFilters: {}
          }
        })
      );
    }
  }, [globalFilters, careplanConfig]);

  const getOrgIdsArr = async (notes: Array<any> = []) => {
    const loadableOrgIds: Set<string> = new Set();
    notes.forEach((note: any) => {
      if (note?.authorOrgId && !loadedOrgIds.includes(note?.authorOrgId)) {
        loadableOrgIds.add(note?.authorOrgId);
      }
    });

    await getOrganizations(Array.from(loadableOrgIds.values()));
  };

  const getOrganizations = async (orgIds: Array<string> = []) => {
    const responses: Array<any> = await Promise.all(
      orgIds.map((orgId: string) => {
        return getOrganization(orgId);
      })
    );
    const organizationsClone = cloneDeep(organizations);
    const loadedOrgIdsClone = cloneDeep(loadedOrgIds);

    responses.forEach((response: any) => {
      if (response) {
        const { status, data } = response;
        if (status == HTTP_STATUS.HTTP_OK) {
          organizationsClone.set(data?.id, data);
          loadedOrgIdsClone.push(data?.id);
        }
      }
    });
    setLoadedOrgIds(loadedOrgIdsClone);
    setOrganizations(organizationsClone);
  };

  useEffect(() => {
    async function init() {
      await fetchDataAndUpdateOptions(setOrgList, carePlanOrgList, getCarePlanOrganizations);
    }
    if (carePlanOrgList.length) {
      updateFilterSetData(carePlanOrgList, 'orgFilter');
    } else {
      init();
    }
  }, [carePlanOrgList]);

  useDidMountEffect(() => {
    setTableHeaders(tableHeadersBuilder());
  }, [organizations]);

  const refreshTableData = (
    name: string = '',
    page: number,
    size: number,
    sort: string = '',
    sortType: string = ''
  ) => {
    setTableValues(name, page, size, sort, sortType);
  };

  const setTableValues = (
    name: string = '',
    page: number,
    size: number,
    sort: string = AppConstants.DEFAULT_SORT_BY,
    sortType: string = AppConstants.DEFAULT_SORT_DESC
  ) => {
    setSize(size);
    setCurrentPage(page);
    setIsLoading(true);
  };

  const elementSelector = (data: any) => {
    if (checkFeaturePermission(AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_UPDATE, false)) {
      const { id } = data;
      dispatch(setNotesData(data));
      navigate(
        RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_VIEW.replace(':id', params?.id || '').replace(
          ':notesId',
          id
        )
      );
    } else {
      dispatch(
        setToastData({
          toastMessage: t('T_USER_NO_PERMISSION_TOAST_MESSAGE').replace(
            'Permission',
            t('T_CARE_PLAN_NOTES')
          ),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const deleteNote = async () => {
    setIsLoading(true);
    let response = await deleteCareplanNote(params?.id || careplanData?.id, deleteId);

    setDeleteId('');
    setDeletePopup(false);
    if ([HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(response.status)) {
      dispatch(
        setToastData({
          toastMessage: t('T_CARE_PLAN_NOTE_DELETED_SUCCESS'),
          isToastActive: true,
          toastType: 'success'
        })
      );
      const { tableFilters, searchText, currentPage, size, sortName, sortType } =
        globalFilters[tableKey];

      getNotesList(currentPage, size, sortName, sortType, tableFilters);
    } else {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(response),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const updateParamObjAfterRemovePills = (updatedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    updatedFilterList.forEach((obj: any) => {
      initialParamObj[obj.id] = obj.selectedFilter;
    });
    setParamObj(initialParamObj);
    setIsLoading(true);
    const {
      searchText = '',
      currentPage: filterCurrentPage = AppConstants.DEFAULT_PAGE,
      size: filterSize = AppConstants.DEFAULT_PAGE_SIZE,
      sortName = '',
      sortType = ''
    } = globalFilters['careplan-notes-list'] || {};

    dispatch(
      setTableOptions({
        ['careplan-notes-list']: {
          searchText: searchText,
          currentPage: filterCurrentPage,
          size: filterSize,
          sortName: sortName,
          sortType: sortType,
          tableFilters: initialParamObj
        }
      })
    );
  };

  const getDisplayKey = (type: any) => {
    let key = type?.key;
    let element = find(careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES], {
      key
    });
    if (element) {
      let key = element?.apiConfig?.fieldValues;
      return key;
    } else {
      return [];
    }
  };

  const updatePillsDataAfterRemovePills = (updatedFilterList: any) => {
    const pillsData: any = [];
    updatedFilterList.forEach((obj: any) => {
      switch (obj.id) {
        case 'orgFilter':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.name,
              label: 'Organization',
              id: obj.id,
              key: item.id
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
        case 'authorType':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item?.display || item?.value || '',
              label: t('T_CARE_PLAN_NOTES_AUTHOR_TYPE_FILTER'),
              id: item.key,
              key: item.key
              // hideIcon: queryParams.id ? true : false
            });
          });
          break;
        case 'authorId':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item?.[loadNextData?.fieldValues?.[0]] || item?.id,
              label: t('T_CARE_PLAN_NOTES_AUTHOR_FILTER'),
              id: item.key,
              key: item.key
            });
          });
          break;
      }
    });
    setFilterPillsData(pillsData);
  };

  const removeFromPillsFilter = (pillsFilterObj: any) => {
    let initialFilterList: any = [...filterList];
    const initialFilterObj = find(initialFilterList, { id: pillsFilterObj.id });
    if (initialFilterObj.id === 'authorType') {
      initialFilterObj.selectedFilter = [];
      let index = findIndex(filterList, function (item: any) {
        return item?.id === 'authorId';
      });

      if (index !== -1) {
        initialFilterList[index].selectedFilter = [];
        initialFilterList[index].itemsList = [];
      }
    }

    if (initialFilterObj.id === 'authorId') {
      initialFilterObj.selectedFilter = [];
    }

    if (initialFilterObj.id === 'orgFilter') {
      initialFilterObj.selectedFilter = filter(initialFilterObj.selectedFilter, (obj) => {
        return obj.name !== pillsFilterObj.name;
      });
    }
    const updatedFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === pillsFilterObj.id) {
        return { ...obj, initialFilterObj };
      } else {
        return { ...obj };
      }
    });
    updateParamObjAfterRemovePills(updatedFilterList);
    updatePillsDataAfterRemovePills(updatedFilterList);
    setFilterList(updatedFilterList);
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <TableContainer>
        <MenuButtonsPortal>
          <BiButton
            className={'max-w-1/2 mr-2 overflow-hidden whitespace-nowrap text-primary'}
            type="button"
            onClick={() => {
              navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST);
            }}
          >
            {t('T_BACK')}
          </BiButton>
          {checkFeaturePermission(
            AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_CREATE,
            false
          ) && (
            <BiButton
              className={
                'flex flex-row  border border-solid border-primary-disabled py-1.5 text-base text-primary'
              }
              type="button"
              onClick={() => {
                navigate(
                  params?.id
                    ? RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_CREATE.replace(
                        ':id',
                        params?.id
                      )
                    : RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST
                );
              }}
            >
              <div className="mr-2 h-5 w-5 self-center text-center">
                {customAddCircle(useTheme().theme.primaryAppColor)}
              </div>
              {t('T_CARE_PLAN_ADD_A_NOTE')}
            </BiButton>
          )}
        </MenuButtonsPortal>

        <Modal
          showCloseIcon={true}
          width="48rem"
          headerText={t('T_DELETE_CONFIRMATION_TITLE')}
          toggleVisiblity={(value: boolean) => {
            setDeletePopup(value);
          }}
          isVisible={deletePopup}
          body={
            <div>
              <div>{t('T_CARE_PLAN_NOTE_DELETE_PROMPT')}</div>
              <div className="mt-8 flex">
                <BiButton
                  className={'ml-auto mr-2 text-primary'}
                  type="button"
                  onClick={() => {
                    setDeletePopup(false);
                    setDeleteId('');
                  }}
                >
                  {t('T_CANCEL')}
                </BiButton>
                <BiButton
                  className={'bg-primary text-white'}
                  type="button"
                  onClick={() => {
                    deleteNote();
                  }}
                >
                  {t('T_DELETE')}
                </BiButton>
              </div>
            </div>
          }
          headerLeftIcon={<ExclamationIcon className="h-7.5 w-6" />}
        />

        <TemplateTable
          tableData={rows}
          templateSelector={elementSelector}
          cellSelector={elementSelector}
          onRefreshTableData={refreshTableData}
          size={globalFilters?.['careplan-notes-list']?.size?.toString()}
          currentPage={globalFilters?.['careplan-notes-list']?.currentPage}
          isSearchable={false}
          tableHeaders={tableHeaders}
          setTableHeaders={setTableHeaders}
          pagingData={paging}
          containerClass="mt-0"
          showDeleteButton={true}
          theme={useTheme().theme}
          totalDataLength={rows.length}
          isPaginated
          isFilterable
          filtersList={filterList}
          applyFilters={applyFilters}
          pillsData={filterPillsData}
          removeFilter={removeFromPillsFilter}
          defaultPillsLabel={t('T_NOTES_FILTERS_DEFAULT_VIEW')}
          globalFilters={globalFilters}
          tablePage="careplan-notes-list"
        />
      </TableContainer>
    </LoaderContainer>
  );
};

export default CarePlanNotesList;
